import React, { useState, useEffect } from "react";
import { Star, HardHat, ClipboardCheck, MapPin, Phone, Mail } from 'lucide-react';
import { getDatabase, ref, onValue, push, set } from 'firebase/database';
import app from '../../firebase';
import Header from '../../Components/Contractor/ContractorHomeHeader';
import Footer from '../../Components/Mart/Footer/MartFooter';
import CartSidebar from "../../Components/Mart/Cart";
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const db = getDatabase(app);

const ContractorHomePage = () => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [activeRequestId, setActiveRequestId] = useState(null);
  const navigate = useNavigate();

  // Check authentication status
  const isAuthenticated = () => {
    return localStorage.getItem('customerId') !== null;
  };

  // Get customer data safely
  const getCustomerData = () => {
    try {
      return JSON.parse(localStorage.getItem('customerData') || '{}');
    } catch {
      return {};
    }
  };

  // Phone and email handlers
  const handlePhoneClick = (phoneNumber) => window.location.href = `tel:${phoneNumber}`;
  const handleEmailClick = (emailAddress) => window.location.href = `mailto:${emailAddress}`;

  // Professional request handler with toast notifications
  const handleSendRequest = async (contractorId, contractorName) => {
    if (!isAuthenticated()) {
      toast.info('Please sign in to send a request', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
      return;
    }

    try {
      setActiveRequestId(contractorId);
      setSendingRequest(true);

      // Show loading toast
      const toastId = toast.loading('Sending your request...', {
        position: "top-center",
        closeButton: true,
      });

      // Prepare request data
      const customerId = localStorage.getItem('customerId');
      const customerData = getCustomerData();
      const requestData = {
        userId: customerId,
        userName: customerData?.name || 'Anonymous',
        userEmail: localStorage.getItem('customerEmail') || 'No email',
        userPhone: customerData?.phone || 'No phone',
        userLocation: JSON.parse(localStorage.getItem('customerLocation') || '{}'),
        timestamp: Date.now(),
        status: 'pending',
        read: false,
        contractorId,
        contractorName,
      };

      // Send to contractor's requests
      const requestsRef = ref(db, `Contractors/${contractorId}/newRequests`);
      const newRequestRef = push(requestsRef);
      await set(newRequestRef, requestData);

      // Store in user's sent requests
      const userRequestRef = ref(db, `Users/${customerId}/sentRequestsToContractor/${newRequestRef.key}`);
      await set(userRequestRef, { ...requestData, requestId: newRequestRef.key });

      // Update toast to success
      toast.update(toastId, {
        render: 'Request sent successfully! Contractor will connect with you soon. Thanks for your request.',
        type: 'success',
        isLoading: false,
        autoClose: 5000,  // Increased to 5 seconds for better readability
        hideProgressBar: false,
      });

    } catch (error) {
      console.error('Request failed:', error);
      toast.error('Failed to send request. Please try again.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
      });
    } finally {
      setSendingRequest(false);
      setActiveRequestId(null);
    }
  };

  // Fetch contractors
  useEffect(() => {
    const contractorsRef = ref(db, 'Contractors');
    
    const unsubscribe = onValue(contractorsRef, (snapshot) => {
      try {
        const data = snapshot.val();
        const contractorsArray = [];

        if (data) {
          Object.keys(data).forEach(state => {
            Object.keys(data[state]).forEach(city => {
              Object.keys(data[state][city]).forEach(phone => {
                contractorsArray.push({
                  id: `${state}/${city}/${phone}`,
                  phone,
                  state,
                  city,
                  ...data[state][city][phone]
                });
              });
            });
          });
        }

        setContractors(contractorsArray);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }, (error) => {
      setError(error.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Services data
  const contractorServices = [
    {
      name: 'General Contracting',
      icon: <HardHat className="w-6 h-6 text-amber-600" />,
      description: 'Full-service construction management for projects of all sizes'
    },
    {
      name: 'Renovations',
      icon: <ClipboardCheck className="w-6 h-6 text-amber-600" />,
      description: 'Transform existing spaces with expert remodeling services'
    },
    {
      name: 'Custom Homes',
      icon: <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>,
      description: 'Tailored home construction to match your unique vision'
    },
    {
      name: 'Commercial Builds',
      icon: <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>,
      description: 'Expert construction for office, retail, and industrial spaces'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        <Header toggleCart={() => setIsCartOpen(!isCartOpen)} />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        <Header toggleCart={() => setIsCartOpen(!isCartOpen)} />
        <div className="text-center py-12">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-md mx-auto">
            <p>Error loading contractors: {error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      <Header toggleCart={() => setIsCartOpen(!isCartOpen)} />
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <ToastContainer />

      <main>
        {/* Contractor Listings */}
        <section className="py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {contractors.length > 0 ? 'Available Contractors' : 'No Contractors Found'}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {contractors.length > 0 ? 'Browse our network of professionals' : 'Check back later for available contractors'}
              </p>
            </div>

            {contractors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {contractors.map((contractor) => (
                  <motion.div 
                    key={contractor.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100"
                  >
                    {/* Contractor card content */}
                    <div className="relative h-48 bg-gray-200">
                      {contractor.profileImage ? (
                        <img
                          src={contractor.profileImage}
                          alt={contractor.companyName}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://images.unsplash.com/photo-1600585152220-90363fe7e115';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300">
                          <HardHat className="w-12 h-12 text-gray-500" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                        <h3 className="text-white text-xl font-bold">{contractor.companyName || 'Construction Company'}</h3>
                        <p className="text-amber-300 text-sm">{contractor.contractorType || 'General Contractor'}</p>
                      </div>
                    </div>

                    <div className="p-5">
                      {/* Rating and projects */}
                      {(contractor.rating > 0 || contractor.projectsCount > 0) && (
                        <div className="flex items-center mb-3">
                          {contractor.rating > 0 && (
                            <div className="flex mr-2">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  size={16}
                                  className={
                                    i < Math.floor(contractor.rating)
                                      ? "text-amber-400 fill-current"
                                      : "text-gray-300"
                                  }
                                />
                              ))}
                            </div>
                          )}
                          <span className="text-sm text-gray-600">
                            {contractor.rating > 0 && contractor.projectsCount > 0 && (
                              <>
                                {contractor.rating.toFixed(1)} ({contractor.projectsCount} projects)
                              </>
                            )}
                          </span>
                        </div>
                      )}

                      {/* Location and experience */}
                      <div className="flex items-center text-gray-600 mb-4">
                        <MapPin className="w-4 h-4 mr-1 text-amber-500" />
                        <span className="text-sm">
                          {contractor.city}, {contractor.state}
                          {contractor.experience && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{contractor.experience} years</span>
                            </>
                          )}
                        </span>
                      </div>

                      {/* Contact info */}
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Contact:</h4>
                        <div className="flex flex-col space-y-2">
                          <div className="flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-amber-600" />
                            <span className="text-sm">{contractor.phone}</span>
                          </div>
                          {contractor.email && (
                            <div className="flex items-center">
                              <Mail className="w-4 h-4 mr-2 text-amber-600" />
                              <span className="text-sm">{contractor.email}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handlePhoneClick(contractor.phone)} 
                            className="text-amber-600 hover:text-amber-700 p-1 rounded-full hover:bg-amber-100 transition-colors"
                            aria-label="Call contractor"
                          >
                            <Phone className="w-5 h-5" />
                          </button>
                          {contractor.email && (
                            <button
                              onClick={() => handleEmailClick(contractor.email)}
                              className="text-amber-600 hover:text-amber-700 p-1 rounded-full hover:bg-amber-100 transition-colors"
                              aria-label="Email contractor"
                            >
                              <Mail className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                        <motion.button
                          onClick={() => handleSendRequest(contractor.id, contractor.companyName || 'Contractor')}
                          disabled={sendingRequest && activeRequestId === contractor.id}
                          whileTap={{ scale: 0.95 }}
                          className={`relative overflow-hidden px-4 py-2 rounded-lg text-sm font-medium transition duration-300 ${
                            sendingRequest && activeRequestId === contractor.id 
                              ? 'bg-amber-500' 
                              : 'bg-amber-600 hover:bg-amber-700 text-white'
                          }`}
                        >
                          {sendingRequest && activeRequestId === contractor.id ? (
                            <div className="flex items-center justify-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Sending...
                            </div>
                          ) : (
                            'Send Request'
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                <div className="mx-auto h-24 w-24 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <HardHat className="w-12 h-12 text-amber-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">No contractors available</h3>
                <p className="mt-2 text-gray-500 max-w-md mx-auto">
                  Currently there are no contractors in your area. Please check back later.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Services Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-b from-white to-amber-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                Construction Services
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Comprehensive solutions for all your building needs
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {contractorServices.map((service, index) => (
                <motion.div 
                  key={index}
                  whileHover={{ y: -5 }}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-lg mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.name}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContractorHomePage;